import * as Sentry from '@sentry/react';
import { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, useNavigate } from 'react-router';

import { ErrorRoute } from '@/components/ErrorRoute';
import { AppShellErrorRoute } from './components/AppShellErrorRoute';
import { Loading } from './components/Loading';
import { AppShellLayout } from '@/components/AppShellLayout';
import RequireRoles from '@/components/RequireRoles';
import { RoleOptions } from '@paladin-monorepo/common';
import { useAuth, UserProps } from './lib/auth';
import { isInquirerAccount, isPrince2Account } from './lib/permissions';

import LoginPage from './pages/auth/login';
import SignupPage from './pages/auth/signup';
import ThankYouPage from './pages/auth/thank-you';
import CompanyExistsPage from './pages/auth/signup/CompanyExistsPage';
import RequestSentPage from './pages/auth/signup/RequestSentPage';
import ForgotPasswordPage from './pages/auth/forgot-password';
import ResetPasswordPage from './pages/auth/reset-password';
import ErrorPage from './pages/auth/error';
import { ExhibitionPage } from './pages/exhibition/ExhibitionPage';
import { ExhibitionStartPage } from './pages/exhibition/ExhibitionStartPage';
import { DashboardPage } from './pages/DashboardPage';
import { InquiriesListPage } from './pages/inquiries/InquiriesListPage';
import { ProjectsListPage } from './pages/projects/ProjectsListPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { SettingsOverviewPage } from './pages/settings/SettingsOverviewPage';
import { GeneralSettingsTab } from './pages/settings/GeneralSettingsTab';
import { UsersSettingsTab } from './pages/settings/UsersSettingsTab';
import { SignersSettingsTab } from './pages/settings/SignersSettingsTab';
import { CenotvorbaSettingsTab } from './pages/settings/CenotvorbaSettingsTab';
import { ProfileSettingsTab } from './pages/settings/ProfileSettingsTab';
import { BillingSettingsTab } from './pages/settings/BillingSettingsTab';
import { PayoutsSettingsTab } from './pages/settings/PayoutsSettingsTab';
import { ClientPayoutBatchesSettingsTab } from './pages/settings/ClientPayoutBatchesSettingsTab';
import { PaymentsLayout } from './pages/payments/PaymentsLayout';
import { PaymentsAuditTab } from './pages/payments/PaymentsAuditTab';
import { PaymentsEscrowTab } from './pages/payments/PaymentsEscrowTab';
import { PasswordSettingsTab } from './pages/settings/PasswordSettingsTab';
import { SubscriptionTab } from './pages/settings/SubscriptionTab';
import { SubscriptionSuccessPage } from './pages/settings/SubscriptionSuccessPage';
import { ProjectLayout } from './pages/projects/ProjectLayout';
import { ProjectOverviewPage } from './pages/projects/ProjectOverviewPage';
import { ProjectMilestonesPage } from './pages/projects/ProjectMilestonesPage';
import { ProjectPaymentsPage } from './pages/projects/ProjectPaymentsPage';
import { ProjectWorkspaceFilesPage } from './pages/projects/ProjectWorkspaceFilesPage';
import { SupplierOnboardingPage } from '@/pages/onboarding/SupplierOnboardingPage';
import WaitingListPage from '@/pages/WaitingListPage';

const DYNAMIC_IMPORT_RELOAD_KEY = 'paladin:dynamic-import-reload-once';

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(typeof error === 'string' ? error : JSON.stringify(error));
}

function isDynamicImportFailure(error: unknown): boolean {
  const message = toError(error).message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror') ||
    message.includes('importing a module script failed')
  );
}

function recoverDynamicImportFailure(error: unknown): boolean {
  if (!isDynamicImportFailure(error)) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const hasReloaded = sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) === '1';
  if (hasReloaded) {
    sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
    return false;
  }

  sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, '1');
  window.location.reload();
  return true;
}

const lazyPage = <T extends Record<string, unknown>, K extends keyof T>(
  factory: () => Promise<T>,
  name: K,
) => lazy(async () => {
  try {
    const module = await factory();
    sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
    return { default: module[name] as React.ComponentType };
  } catch (error) {
    Sentry.captureException(toError(error), {
      tags: {
        area: 'lazy-route-import',
        exportName: String(name),
      },
    });
    recoverDynamicImportFailure(error);
    throw error;
  }
});

const LazyApprovalsPage = lazyPage(
  () => import('./pages/approvals/ApprovalsPage'),
  'ApprovalsPage',
);
const LazyCostsPage = lazyPage(
  () => import('./pages/costs/CostsPage'),
  'CostsPage',
);
const LazyProjectTolerancesPage = lazyPage(
  () => import('./pages/projects/ProjectTolerancesPage'),
  'ProjectTolerancesPage',
);
const LazyProjectCostsPage = lazyPage(
  () => import('./pages/projects/ProjectCostsPage'),
  'ProjectCostsPage',
);
const LazyProjectRegistersPage = lazyPage(
  () => import('./pages/projects/ProjectRegistersPage'),
  'ProjectRegistersPage',
);
const LazyProjectMeasurementsPage = lazyPage(
  () => import('./pages/projects/ProjectMeasurementsPage'),
  'ProjectMeasurementsPage',
);
const LazyProjectChangeRequestsPage = lazyPage(
  () => import('./pages/projects/ProjectChangeRequestsPage'),
  'ProjectChangeRequestsPage',
);
const LazyProjectBusinessCasePage = lazyPage(
  () => import('./pages/projects/ProjectBusinessCasePage'),
  'ProjectBusinessCasePage',
);
const LazyProjectLogPage = lazyPage(
  () => import('./pages/projects/ProjectLogPage'),
  'ProjectLogPage',
);
const LazyInquiryDetailPage = lazyPage(
  () => import('./pages/inquiries/InquiryDetailPage'),
  'InquiryDetailPage',
);
const LazyCreateInquiryPage = lazyPage(
  () => import('./pages/inquiries/CreateInquiryPage'),
  'CreateInquiryPage',
);
const LazyEditInquiryPage = lazyPage(
  () => import('./pages/inquiries/EditInquiryPage'),
  'EditInquiryPage',
);
const LazyInquiryOffersPage = lazyPage(
  () => import('./pages/inquiries/InquiryOffersPage'),
  'InquiryOffersPage',
);
const LazyCreateOfferPage = lazyPage(
  () => import('./pages/offers/CreateOfferPage'),
  'CreateOfferPage',
);
const LazyEditOfferPage = lazyPage(
  () => import('./pages/offers/EditOfferPage'),
  'EditOfferPage',
);
const LazyOfferDetailPage = lazyPage(
  () => import('./pages/offers/OfferDetailPage'),
  'OfferDetailPage',
);
const LazyProjectContractsPage = lazyPage(
  () => import('./pages/projects/ProjectContractsPage'),
  'ProjectContractsPage',
);
const LazyProjectContractsComparePage = lazyPage(
  () => import('./pages/projects/ProjectContractsComparePage'),
  'ProjectContractsComparePage',
);
const LazyLlmChatSessionPage = lazyPage(
  () => import('./pages/llm-chat/LlmChatSessionPage'),
  'LlmChatSessionPage',
);
const LazyNewDecisionChatPage = lazyPage(
  () => import('./pages/llm-chat/NewDecisionChatPage'),
  'NewDecisionChatPage',
);
const LazyMonomoAiPage = lazyPage(
  () => import('./pages/monomo-ai/MonomoAiPage'),
  'MonomoAiPage',
);
const LazyDecisionCaseRedirectPage = lazyPage(
  () => import('./pages/decision-cases/DecisionCaseRedirectPage'),
  'DecisionCaseRedirectPage',
);
const LazyCreateUserAgentConfigPage = lazyPage(
  () => import('./pages/monomo-ai/CreateUserAgentConfigPage'),
  'CreateUserAgentConfigPage',
);
const LazyEditUserAgentConfigPage = lazyPage(
  () => import('./pages/monomo-ai/EditUserAgentConfigPage'),
  'EditUserAgentConfigPage',
);
const LazyCreateProjectConceptPage = lazyPage(
  () => import('./pages/project-concepts/CreateProjectConceptPage'),
  'CreateProjectConceptPage',
);
const LazyEditProjectConceptPage = lazyPage(
  () => import('./pages/project-concepts/EditProjectConceptPage'),
  'EditProjectConceptPage',
);
const LazyDesignSystemPage = lazyPage(
  () => import('./pages/DesignSystemPage'),
  'DesignSystemPage',
);
const LazyAdminDashboardPage = lazyPage(
  () => import('./pages/admin/Dashboard'),
  'default',
);
const LazyAdminAccountsListPage = lazyPage(
  () => import('@/pages/admin/AccountsList'),
  'default',
);
const LazyAdminAccountDetailPage = lazyPage(
  () => import('@/pages/admin/AccountDetail'),
  'default',
);
const LazyAdminPersonDetailPage = lazyPage(
  () => import('@/pages/admin/PersonDetail'),
  'default',
);
const LazyAdminInquiriesListPage = lazyPage(
  () => import('@/pages/admin/InquiriesList'),
  'default',
);
const LazyAdminInquiryDetailPage = lazyPage(
  () => import('@/pages/admin/InquiryDetail'),
  'default',
);
const LazyAdminOfferDetailPage = lazyPage(
  () => import('@/pages/admin/OfferDetail'),
  'default',
);
const LazyAdminDigiSignEnvelopesListPage = lazyPage(
  () => import('@/pages/admin/digisign/EnvelopesList'),
  'default',
);
const LazyAdminDigiSignEnvelopeDetailPage = lazyPage(
  () => import('@/pages/admin/digisign/EnvelopeDetail'),
  'default',
);
const LazyAdminDigiSignWebhooksListPage = lazyPage(
  () => import('@/pages/admin/digisign/WebhooksList'),
  'default',
);
const LazyAdminSessionsListPage = lazyPage(
  () => import('@/pages/admin/SessionsList'),
  'default',
);
const LazyAdminProjectsListPage = lazyPage(
  () => import('@/pages/admin/ProjectsList'),
  'default',
);
const LazyAdminAgentConfigsListPage = lazyPage(
  () => import('@/pages/admin/AgentConfigsList'),
  'default',
);
const LazyCreateAdminAgentConfigPage = lazyPage(
  () => import('@/pages/admin/CreateAdminAgentConfigPage'),
  'default',
);
const LazyEditAdminAgentConfigPage = lazyPage(
  () => import('@/pages/admin/EditAdminAgentConfigPage'),
  'default',
);
const LazyAdminFeeSchedulePage = lazyPage(
  () => import('@/pages/admin/FeeSchedulePage'),
  'default',
);
const LazyAdminSubscriptionPlansPage = lazyPage(
  () => import('@/pages/admin/SubscriptionPlansPage'),
  'default',
);
const LazyAdminCredentialReviewsPage = lazyPage(
  () => import('@/pages/admin/CredentialReviewsPage'),
  'default',
);
const LazyAdminObservationTypesPage = lazyPage(
  () => import('@/pages/admin/ObservationTypesPage'),
  'default',
);
const LazyAdminPayoutBankAccountReviewsPage = lazyPage(
  () => import('@/pages/admin/PayoutBankAccountReviewsPage'),
  'default',
);
const LazyAdminLlmUsagePage = lazyPage(
  () => import('@/pages/admin/LlmUsagePage'),
  'default',
);
const LazyAdminLlmModelsPage = lazyPage(
  () => import('@/pages/admin/LlmModelsPage'),
  'AdminLlmModelsPage',
);
const LazyAdminLlmChatsListPage = lazyPage(
  () => import('@/pages/admin/LlmChatsListPage'),
  'default',
);
const LazyAdminFeedbackListPage = lazyPage(
  () => import('@/pages/admin/FeedbackListPage'),
  'default',
);
const LazyAdminDisputesListPage = lazyPage(
  () => import('@/pages/admin/DisputesListPage'),
  'default',
);
const LazyAdminRefundRequestsListPage = lazyPage(
  () => import('@/pages/admin/RefundRequestsListPage'),
  'default',
);
const LazyAdminSmtpPage = lazyPage(
  () => import('@/pages/admin/SmtpPage'),
  'default',
);

function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const ProtectedRoute = ({ condition, children }: { condition: (user: UserProps) => boolean, children: React.ReactNode }) => {
  const navigate = useNavigate()
  const { user, isLoading } = useAuth({ middleware: 'auth', redirectTo: '/auth/login', tmp: 'ProtectedRoute' });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!user) {
      navigate('/')
      return;
    }

    if (!condition(user)) {
      navigate('/');
    }
    // navigate is stable from react-router and doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, condition]);

  if (isLoading) {
    return <Loading />;
  }

  return children;
};

export const router = sentryCreateBrowserRouter(
  [
    {
      path: '/auth',
      element: <Outlet />,
      errorElement: <ErrorRoute />,
      children: [
        {
          path: 'login',
          element: <LoginPage />,
        },
        {
          path: 'signup',
          element: <Outlet />,
          children: [
            { index: true, element: <SignupPage /> },
            {
              path: 'thank-you',
              element: <ThankYouPage />,
            },
            {
              path: 'company-exists',
              element: <CompanyExistsPage />,
            },
            {
              path: 'request-sent',
              element: <RequestSentPage />,
            },
          ],
        },
        {
          path: 'forgot-password',
          element: <ForgotPasswordPage />,
        },
        {
          path: 'reset-password',
          element: <ResetPasswordPage />,
        },
        {
          path: 'error',
          element: <ErrorPage />,
        },
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
    {
      path: '/exhibition',
      element: <ExhibitionPage />,
      errorElement: <ErrorRoute />,
    },
    {
      element: <AppShellLayout />,
      errorElement: <AppShellErrorRoute />,
      children: [
    {
      path: '/exhibition/start',
      element: <ExhibitionStartPage />,
    },
    {
      path: '/waiting-list',
      element: <WaitingListPage />,
    },
    {
      path: '/onboarding',
      element: <SupplierOnboardingPage />,
    },
    {
      path: '/design-system',
      element: <LazyRoute><LazyDesignSystemPage /></LazyRoute>,
    },
    {
      path: '/settings',
      element: <SettingsLayout />,
      children: [
        { index: true, element: <SettingsOverviewPage /> },
        {
          path: 'general',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <GeneralSettingsTab />
            </RequireRoles>
          ),
        },
        {
          path: 'users',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <UsersSettingsTab />
            </RequireRoles>
          ),
        },
        {
          path: 'signers',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <SignersSettingsTab />
            </RequireRoles>
          ),
        },
        {
          path: 'cenotvorba',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <CenotvorbaSettingsTab />
            </RequireRoles>
          ),
        },
        { path: 'profile', element: <ProfileSettingsTab /> },
        {
          path: 'payouts',
          element: (
            <RequireRoles
              roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN, RoleOptions.EMPLOYEE]}
            >
              <PayoutsSettingsTab />
            </RequireRoles>
          ),
        },
        {
          path: 'payout-batches',
          element: <Navigate to="/payments/batches" replace />,
        },
        {
          path: 'billing',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <BillingSettingsTab />
            </RequireRoles>
          ),
        },
        { path: 'password', element: <PasswordSettingsTab /> },
        {
          path: 'subscription',
          element: (
            <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
              <SubscriptionTab />
            </RequireRoles>
          ),
        },
        {
          path: 'subscription/success',
          element: <SubscriptionSuccessPage />,
        },
      ],
    },
    {
      path: '/payments',
      element: (
        <RequireRoles roles={[RoleOptions.ACCOUNT_OWNER, RoleOptions.ACCOUNT_ADMIN]}>
          <ProtectedRoute condition={(u) => isInquirerAccount(u)}>
            <PaymentsLayout />
          </ProtectedRoute>
        </RequireRoles>
      ),
      children: [
        { index: true, element: <Navigate to="/payments/audit" replace /> },
        { path: 'audit', element: <PaymentsAuditTab /> },
        { path: 'batches', element: <ClientPayoutBatchesSettingsTab /> },
        { path: 'escrow', element: <PaymentsEscrowTab /> },
      ],
    },
    {
      path: '/inquiries',
      element: <InquiriesListPage />,
    },
    {
      path: '/inquiries/new',
      element: <LazyRoute><LazyCreateInquiryPage /></LazyRoute>,
    },
    {
      path: '/inquiries/:id',
      element: <LazyRoute><LazyInquiryDetailPage /></LazyRoute>,
    },
    {
      path: '/inquiries/:id/edit',
      element: <LazyRoute><LazyEditInquiryPage /></LazyRoute>,
    },
    {
      path: '/projects',
      element: <ProjectsListPage />,
    },
    {
      path: '/approvals',
      element: <LazyRoute><LazyApprovalsPage /></LazyRoute>,
    },
    {
      path: '/costs',
      element: <ProtectedRoute condition={isPrince2Account}><LazyRoute><LazyCostsPage /></LazyRoute></ProtectedRoute>,
    },
    {
      path: '/project-concepts/new',
      element: <LazyRoute><LazyCreateProjectConceptPage /></LazyRoute>,
    },
    {
      path: '/project-concepts/:id/edit',
      element: <LazyRoute><LazyEditProjectConceptPage /></LazyRoute>,
    },
    {
      path: '/inquiries/:inquiryId/offers',
      element: <ProtectedRoute condition={isInquirerAccount}><LazyRoute><LazyInquiryOffersPage /></LazyRoute></ProtectedRoute>,
    },
    {
      path: '/inquiries/:inquiryId/offers/new',
      element: <LazyRoute><LazyCreateOfferPage /></LazyRoute>,
    },
    {
      path: '/inquiries/:inquiryId/offers/:offerId/edit',
      element: <LazyRoute><LazyEditOfferPage /></LazyRoute>,
    },
    {
      path: '/offers/:id',
      element: <LazyRoute><LazyOfferDetailPage /></LazyRoute>,
    },
    {
      path: '/monomo-ai',
      element: <LazyRoute><LazyMonomoAiPage /></LazyRoute>,
    },
    {
      path: '/decision-cases',
      element: <Navigate to="/monomo-ai" replace />,
    },
    {
      path: '/decision-cases/new',
      element: <Navigate to="/monomo-ai" replace />,
    },
    {
      path: '/decision-cases/:id',
      element: <LazyRoute><LazyDecisionCaseRedirectPage /></LazyRoute>,
    },
    {
      path: '/monomo-ai/new',
      element: <LazyRoute><LazyCreateUserAgentConfigPage /></LazyRoute>,
    },
    {
      path: '/monomo-ai/:id/edit',
      element: <LazyRoute><LazyEditUserAgentConfigPage /></LazyRoute>,
    },
    {
      path: '/llm-chat',
      element: <Navigate to="/monomo-ai" replace />,
    },
    {
      path: '/llm-chat/new',
      element: <LazyRoute><LazyNewDecisionChatPage /></LazyRoute>,
    },
    {
      path: '/llm-chat/:sessionId',
      element: <LazyRoute><LazyLlmChatSessionPage /></LazyRoute>,
    },
    {
      path: '/admin',
      element: (
        <RequireRoles roles={[RoleOptions.SUPERADMIN, RoleOptions.SUPPORT]}>
          <Outlet />
        </RequireRoles>
      ),
      children: [
        {
          path: '',
          element: <LazyRoute><LazyAdminDashboardPage /></LazyRoute>,
        },
        {
          path: 'accounts',
          element: <LazyRoute><LazyAdminAccountsListPage /></LazyRoute>,
        },
        {
          path: 'accounts/:id',
          element: <LazyRoute><LazyAdminAccountDetailPage /></LazyRoute>,
        },
        {
          path: 'persons/:personId',
          element: <LazyRoute><LazyAdminPersonDetailPage /></LazyRoute>,
        },
        {
          path: 'inquiries',
          element: <LazyRoute><LazyAdminInquiriesListPage /></LazyRoute>,
        },
        {
          path: 'inquiries/:id',
          element: <LazyRoute><LazyAdminInquiryDetailPage /></LazyRoute>,
        },
        {
          path: 'offers/:id',
          element: <LazyRoute><LazyAdminOfferDetailPage /></LazyRoute>,
        },
        {
          path: 'projects',
          element: <LazyRoute><LazyAdminProjectsListPage /></LazyRoute>,
        },
        {
          path: 'sessions',
          element: <LazyRoute><LazyAdminSessionsListPage /></LazyRoute>,
        },
        {
          path: 'llm-usage',
          element: <LazyRoute><LazyAdminLlmUsagePage /></LazyRoute>,
        },
        {
          path: 'llm-models',
          element: <LazyRoute><LazyAdminLlmModelsPage /></LazyRoute>,
        },
        {
          path: 'llm-chats',
          element: <LazyRoute><LazyAdminLlmChatsListPage /></LazyRoute>,
        },
        {
          path: 'agent-configs',
          element: <LazyRoute><LazyAdminAgentConfigsListPage /></LazyRoute>,
        },
        {
          path: 'agent-configs/new',
          element: <LazyRoute><LazyCreateAdminAgentConfigPage /></LazyRoute>,
        },
        {
          path: 'agent-configs/:id/edit',
          element: <LazyRoute><LazyEditAdminAgentConfigPage /></LazyRoute>,
        },
        {
          path: 'fees',
          element: <LazyRoute><LazyAdminFeeSchedulePage /></LazyRoute>,
        },
        {
          path: 'subscription-plans',
          element: <LazyRoute><LazyAdminSubscriptionPlansPage /></LazyRoute>,
        },
        {
          path: 'feedback',
          element: <LazyRoute><LazyAdminFeedbackListPage /></LazyRoute>,
        },
        {
          path: 'credential-reviews',
          element: <LazyRoute><LazyAdminCredentialReviewsPage /></LazyRoute>,
        },
        {
          path: 'observation-types',
          element: <LazyRoute><LazyAdminObservationTypesPage /></LazyRoute>,
        },
        {
          path: 'payout-bank-accounts',
          element: <LazyRoute><LazyAdminPayoutBankAccountReviewsPage /></LazyRoute>,
        },
        {
          path: 'disputes',
          element: <LazyRoute><LazyAdminDisputesListPage /></LazyRoute>,
        },
        {
          path: 'refund-requests',
          element: <LazyRoute><LazyAdminRefundRequestsListPage /></LazyRoute>,
        },
        {
          path: 'smtp',
          element: <LazyRoute><LazyAdminSmtpPage /></LazyRoute>,
        },
        {
          path: 'digisign',
          children: [
            {
              path: 'envelopes',
              element: <LazyRoute><LazyAdminDigiSignEnvelopesListPage /></LazyRoute>,
            },
            {
              path: 'envelopes/:id',
              element: <LazyRoute><LazyAdminDigiSignEnvelopeDetailPage /></LazyRoute>,
            },
            {
              path: 'webhooks',
              element: <LazyRoute><LazyAdminDigiSignWebhooksListPage /></LazyRoute>,
            },
          ],
        },
      ],
    },
    {
      path: '/projects/:projectId/workspace',
      element: <ProjectLayout />,
      children: [
        {
          index: true,
          element: <ProjectOverviewPage />,
        },
        {
          path: 'milestones',
          element: <ProjectMilestonesPage />,
        },
        {
          path: 'payments',
          element: <ProjectPaymentsPage />,
        },
        {
          path: 'contracts',
          element: <LazyRoute><LazyProjectContractsPage /></LazyRoute>,
        },
        {
          path: 'contracts/compare',
          element: <LazyRoute><LazyProjectContractsComparePage /></LazyRoute>,
        },
        {
          path: 'documents',
          element: <ProjectWorkspaceFilesPage />,
        },
        {
          path: 'tolerances',
          element: <LazyRoute><LazyProjectTolerancesPage /></LazyRoute>,
        },
        {
          path: 'costs',
          element: <LazyRoute><LazyProjectCostsPage /></LazyRoute>,
        },
        {
          path: 'risks',
          element: <LazyRoute><LazyProjectRegistersPage /></LazyRoute>,
        },
        {
          path: 'measurements',
          element: <LazyRoute><LazyProjectMeasurementsPage /></LazyRoute>,
        },
        {
          path: 'changes',
          element: <LazyRoute><LazyProjectChangeRequestsPage /></LazyRoute>,
        },
        {
          path: 'business-case',
          element: <LazyRoute><LazyProjectBusinessCasePage /></LazyRoute>,
        },
        {
          path: 'log',
          element: <LazyRoute><LazyProjectLogPage /></LazyRoute>,
        },
      ],
    },
    {
      path: '/',
      element: <DashboardPage />,
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
    //   {
    //     path: '/maintenance',
    //     element: <MaintenancePage />,
    //   },
      ],
    },
  ],
);
